import {
  AUTH_JWT_TOKEN,
  AUTH_JWT_TOKEN_EXPIRATION,
  AUTH_REFRESH_TOKEN,
  AUTH_REFRESH_TOKEN_EXPIRATION,
  UpdateIconButton,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  newToast,
  pn,
  Typography,
  Stack,
} from "@compass/core";

import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import { ListBeProperty } from "components";
import {
  AuthResult,
  LkRoleId,
  Permission,
  PermissionAction,
  SortObject,
  UserListDto,
  UserListResource,
} from "generated/openApi";

import { useDateTime } from "hooks";
import { homePageRoute } from "pages/HomePage/HomePage";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { RootState } from "redux/store";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { userPageRoute } from "../UserPage/UserPage";

export const UserTable = (props: {
  rootPermission: Permission;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  users: UserListDto[];
  resource: UserListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const appRoleId = useSelector((state: RootState) => state.app.roleId);
  const properties = props.resource.properties || [];
  const formatDateTime = useDateTime();

  const loginAsUser = (userId: number) => {
    if (window.confirm("You are about to login as this user. Continue?")) {
      ApiService.users.authenticateAsAnotherUser(userId).then((response) => {
        const authResult: AuthResult = response;

        localStorage.setItem(AUTH_JWT_TOKEN, authResult.jwtToken!);
        localStorage.setItem(AUTH_JWT_TOKEN_EXPIRATION, authResult.jwtTokenExpiration?.toString()!);
        localStorage.setItem(AUTH_REFRESH_TOKEN, authResult.refreshToken!);
        localStorage.setItem(AUTH_REFRESH_TOKEN_EXPIRATION, authResult.refreshTokenExpiration?.toString()!);

        navigate(`/${homePageRoute.getPath()}`);
        window.location.reload();
      });
    }
  };

  const sendInvitationEmail = (userId: number) => {
    if (window.confirm("You are about to send this user invitation email. Continue?")) {
      ApiService.users.sendInvitationEmail(userId).then((response) => {
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<UserListDto>("username"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<UserListDto>("role"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<UserListDto>("lastName"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<UserListDto>("firstName"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<UserListDto>("email"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<UserListDto>("lastLoginDate"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.users.map((userItem) => (
            <TableRow
              key={userItem.id}
              onClick={() => navigate(userPageRoute.getPath({ id: userItem.id, crudMode: "view" }))}
            >
              <TableCell>
                <Stack>
                  <Typography variant="inherit">{userItem.username}</Typography>
                  {userItem.deactivated && (
                    <Typography color="error" variant="caption">
                      Deactivated
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell>{userItem.role}</TableCell>
              <TableCell>
                {userItem.lastName}, {userItem.firstName}
              </TableCell>
              <TableCell>{userItem.email}</TableCell>
              <TableCell>{formatDateTime(userItem.lastLoginDate)}</TableCell>
              <TableCell type="action">
                {isAuthorized(props.rootPermission, PermissionAction.Update) && (
                  <UpdateIconButton
                    onClick={() => navigate(userPageRoute.getPath({ id: userItem.id, crudMode: "edit" }))}
                  />
                )}

                {isAuthorized(props.rootPermission, PermissionAction.Create) && (
                  <IconButton
                    onClick={() => sendInvitationEmail(userItem.id)}
                    color="secondary"
                    title="Send registration email to user"
                    disabled={userItem.registeredDate !== null}
                  >
                    <ForwardToInboxIcon />
                  </IconButton>
                )}

                {appRoleId === Number(LkRoleId.Cobro) && (
                  <IconButton
                    onClick={() => loginAsUser(userItem.id)}
                    disabled={userItem.deactivated}
                    color="secondary"
                    title="Login as this user"
                  >
                    <ArrowCircleRightIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
